import { ofType, combineEpics } from 'redux-observable';
import {withLatestFrom, flatMap, catchError, mergeMap} from 'rxjs/operators';
import {of, from, EMPTY, merge} from 'rxjs';


import { ApplicationRequestAPI } from 'api/application_request';

import { ApplicationAPI } from 'api/application';
import { UsersAPI } from 'api/users';

import * as ApplicationRequestActions from './actions';
import { ActionTypes } from './actionTypes';

import { checkDiscount, maxApprove } from './discount_helper'
import { addMetrix } from './metrix_helper'
import { waitForBlackbox } from 'utils/blackbox'

import * as WidgetActions from 'containers/EmbeddableWidget/actions';
import {ProvidersApi} from "../../api/providers";
import {isPrequalification} from '../../utils/helpers';

const init = (action$, state$) => action$.pipe(
  ofType(ActionTypes.init),
  withLatestFrom(state$),
  flatMap(([ action, state ]) => {
    if (state.widget.inIframe) {
      return [];
    }
    return [
      ApplicationRequestActions.initUserData(action.payload)
    ]
  }),
);

const initApplication = (action$, state$) => action$.pipe(
  ofType(ActionTypes.initApplication),
  withLatestFrom(state$),
  flatMap(([ action, state ]) => {
    const companyId = state.leaseForm.applicationRequest.companyId;
    const b2b = state.leaseForm.applicationRequest.b2b === true;

    const blackboxPromise = state.leaseForm.blackboxId
      ? Promise.resolve(state.leaseForm.blackboxId)
      : waitForBlackbox(10000);

    return from(blackboxPromise).pipe(
      flatMap(function (resolvedBlackboxId) {
        const requestDate = addMetrix(state);
        if (resolvedBlackboxId && !requestDate.blackboxId) {
          requestDate.blackboxId = resolvedBlackboxId;
        }
        if (companyId) {
          requestDate.companyId = companyId;
        }

        return from(ApplicationRequestAPI.init(
          state.widget.key,
          {application: requestDate}
        )).pipe(
          flatMap(data => {
            if (data.errors) {
              return b2b
                ? of(ApplicationRequestActions.b2bOfferFailed())
                : of(ApplicationRequestActions.initApplicationFailed())
            }

            const prequalification = isPrequalification(state.leaseForm.applicationRequest.prequalification);
            if (b2b && (!data.offers || data.offers.length === 0 || !data.offers[0]?.approve_status)) {
              if (prequalification) {
                return of(ApplicationRequestActions.initApplicationFailed());
              }
              return of(ApplicationRequestActions.b2bOfferFailed());
            }

            if (requestDate.provider_name == 'flexshopper') {
              data.offers[0].additional_info_data = requestDate.additional_info_data;
            }

            const discountOffers = checkDiscount(data, state);

            if (discountOffers.offers.length > 0 && !requestDate.promoApplied) {
              return [
                ApplicationRequestActions.applyDiscount({data, discountOffers})
              ]
            }

            const maxApproveValue = maxApprove(data)
            window.parent.postMessage({type: "maxAmount", amount: maxApproveValue}, '*')
            return [
              ApplicationRequestActions.setMaxApprove(maxApproveValue),
              ApplicationRequestActions.initApplicationSuccess({data}),
              ApplicationRequestActions.processApplicationResponse(data)
            ]
          }),
          catchError(err => {
            const b2b = state.leaseForm.applicationRequest.b2b === true;
            return b2b
              ? of(ApplicationRequestActions.b2bOfferFailed())
              : of(ApplicationRequestActions.initApplicationFailed())
          })
        )
      })
    )
  }),
);

const applyDiscount = (action$, state$) => action$.pipe(
  ofType(ActionTypes.applyDiscount),
  withLatestFrom(state$),
  flatMap(([action, state]) => {
    return from(ApplicationRequestAPI.discount(
      state.widget.key,
      state.leaseForm.applicationRequest.orderId,
      action.payload.discountOffers.percentage,
      action.payload.discountOffers.approved_amount,
    ))
      .pipe(
        flatMap((data) => {
          const items = data.items
          return [
            ApplicationRequestActions.updateCart(items),
            ApplicationRequestActions.updateWithDiscount({data: action.payload.discountOffers.offers})
          ]
        }),
        catchError(err => {
          return of(ApplicationRequestActions.processApplicationResponse(action.payload.data)
          )})
      )
  })
)

const updateWithDiscount = (action$, state$) => action$.pipe(
  ofType(ActionTypes.updateWithDiscount),
  withLatestFrom(state$),
  flatMap(([action, state]) => {
    return from(ApplicationRequestAPI.updateWithDiscount(
      state.widget.key,
      state.leaseForm.requestId,
      {application: state.leaseForm.applicationRequest, providers: state.leaseForm.discountProviders}
    ))
      .pipe(
        flatMap((data) => {
          if (data.errors) {
            return of(ApplicationRequestActions.initApplicationFailed())
          }

          if (state.leaseForm.applicationRequest.provider_name == 'flexshopper') {
            data.offers[0].additional_info_data = state.leaseForm.applicationRequest.additional_info_data;
          }

          return [
            ApplicationRequestActions.initApplicationSuccess({data}),
            ApplicationRequestActions.processApplicationResponse(data)
          ]
        }),
        catchError(err => {
          return of(ApplicationRequestActions.processApplicationResponse(action.payload.data)
          )})
      )
  })
)

const editCart = (action$, state$) => action$.pipe(
  ofType(ActionTypes.editCart),
  withLatestFrom(state$),
  flatMap(([action, state]) => {
    return from(ApplicationRequestAPI.editCart(
      state.widget.key,
      state.leaseForm.applicationRequest.orderId,
      action.payload
    ))
      .pipe(
        flatMap((data) => {
          if (data.errors || data.error) {
            return of(ApplicationRequestActions.editCartFailed())
          }

          const items = data.items
          return [
            ApplicationRequestActions.updateCart(items),
            ApplicationRequestActions.updateWithRecommendations(),
          ];
        }),
        catchError(err => {
          return of(ApplicationRequestActions.editCartFailed());
        })
      )
  })
)

const prequalEditCart = (action$, state$) => action$.pipe(
  ofType(ActionTypes.prequalEditCart),
  withLatestFrom(state$),
  flatMap(([action, state]) => {
    const prequalData = state.leaseForm.prequalData || {};

    return from(ApplicationRequestAPI.editCart(
      state.widget.key,
      state.leaseForm.applicationRequest.orderId,
      action.payload
    ))
      .pipe(
        flatMap((data) => {
          if (data.errors || data.error) {
            return of(ApplicationRequestActions.editCartFailed())
          }

          const items = data.items
          const cartTotal = (items || []).reduce(
            (sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0
          );
          const lookup = { ...(prequalData.lookup || {}), items };
          const staleData = { ...prequalData, cart_total: cartTotal, lookup };

          return from(ProvidersApi.progressivePrequalLookup(state.widget.key, lookup))
            .pipe(
              flatMap((fresh) => {
                const nextData = (fresh && (fresh.status === 'approved' || fresh.status === 'prequalified'))
                  ? { ...fresh, cart_total: cartTotal, lookup }
                  : staleData;
                return [
                  ApplicationRequestActions.prequalCartUpdated(items),
                  ApplicationRequestActions.setPrequalData(nextData),
                ];
              }),
              catchError(() => [
                ApplicationRequestActions.prequalCartUpdated(items),
                ApplicationRequestActions.setPrequalData(staleData),
              ])
            );
        }),
        catchError(err => {
          return of(ApplicationRequestActions.editCartFailed());
        })
      )
  })
)

const updateWithRecommendation = (action$, state$) => action$.pipe(
  ofType(ActionTypes.updateWithRecommendations),
  withLatestFrom(state$),
  flatMap(([action, state]) => {
    return from(ApplicationRequestAPI.update_cart(
      state.widget.key,
      state.leaseForm.requestId,
      {application: state.leaseForm.applicationRequest}
    ))
      .pipe(
        flatMap((data) => {
          if (data.errors) {
            return [
              ApplicationRequestActions.editCartFailed({ keepCart: true }),
              ApplicationRequestActions.processApplicationResponse(state.leaseForm.applicationResponse),
            ]
          }

          return [
            ApplicationRequestActions.initApplicationSuccess({data}),
            ApplicationRequestActions.cartSubmitSuccess(),
            ApplicationRequestActions.processApplicationResponse(data)
          ]
        }),
        catchError(err => {
          return of(
            ApplicationRequestActions.editCartFailed({ keepCart: true }),
            ApplicationRequestActions.processApplicationResponse(state.leaseForm.applicationResponse)
          );
        })
      )
  })
)

const getRecommendationsForItem = (action$, state$) => action$.pipe(
  ofType(ActionTypes.getRecommendationsForItem),
  withLatestFrom(state$),
  flatMap(([action, state]) => {
    const { itemId, approvedAmount, remainingDeficit } = action.payload;
    return from(ApplicationRequestAPI.recommendations(
      state.widget.key,
      state.leaseForm.applicationRequest.orderId,
      approvedAmount,
      itemId,
      remainingDeficit
    ))
      .pipe(
        flatMap((data) => {
          if (data.errors || data.error) {
            return of(ApplicationRequestActions.setRecommendationsForItem({
              itemId,
              approvedAmount,
              remainingDeficit,
              data: { recommendations: [] },
              error: data.errors || data.error,
            }));
          }
          return of(ApplicationRequestActions.setRecommendationsForItem({
            itemId,
            approvedAmount,
            remainingDeficit,
            data,
          }));
        }),
        catchError(err => of(ApplicationRequestActions.setRecommendationsForItem({
          itemId,
          approvedAmount,
          remainingDeficit,
          data: { recommendations: [] },
          error: err && err.message,
        })))
      )
  })
);

const processApplicationResponse = (action$, state$) => action$.pipe(
  ofType(ActionTypes.processApplicationResponse),
  withLatestFrom(state$),
  flatMap(([ action, state ]) => {
    const data = action.payload

    if (!data.offers || data.offers.length === 0) {
      return of(ApplicationRequestActions.nextStep({step: 'nobodyApprovedStep'}))
    }

    const approved_not_finished = data.offers.filter(o => o.approve_status === 'approved' && o.is_finished === false);
    const need_additionals = data.offers.filter(o => o.approve_status === 'need_additionals');
    const affiliated_provider = state.leaseForm.applicationRequest.provider_name;
    const finished = data.offers.find(o => o.approve_status === 'approved' && o.is_finished === true);

    if (finished) {
      return of(ApplicationRequestActions.nextStep({
        step: 'alreadyCompletedStep',
        data: { app: finished }
      }))
    }

    if (approved_not_finished.length === 0) {
      if (need_additionals.length > 0 && affiliated_provider == 'anybody') {
        return of(ApplicationRequestActions.nextStep({step:'additionalInfoStep'}))
      }

      return of(ApplicationRequestActions.nextStep({step:'nobodyApprovedStep'}))
    }
    const prequalification = isPrequalification(state.leaseForm.applicationRequest.prequalification)
    const nextStep = prequalification ? 'step3Prequalification' : 'form3'

    return of(ApplicationRequestActions.nextStep({step: nextStep}))
  })
);

const continueInitApplication = (action$, state$) => action$.pipe(
  ofType(ActionTypes.continueInitApplication),
  withLatestFrom(state$),
  flatMap(([ action, state ]) => {
    return from(ApplicationRequestAPI.continue(
      state.widget.key,
      state.leaseForm.requestId,
      {application: state.leaseForm.applicationRequest}))
      .pipe(
        flatMap(data => {
          if (data.errors) {
            return of(ApplicationRequestActions.initApplicationFailed())
          }

          const discountOffers = checkDiscount(data, state);
          if (discountOffers.offers.length > 0 && !state.leaseForm.applicationRequest.promoApplied) {
            return [
              ApplicationRequestActions.applyDiscount({data, discountOffers})
            ]
          }

          return [
            ApplicationRequestActions.continueInitApplicationSuccess({data}),
            ApplicationRequestActions.processApplicationResponse(data)
          ]

        }),
        catchError(err => {
          return of(ApplicationRequestActions.initApplicationFailed()
          )}),
      )
  }),
);


// const acceptOffer = (action$, state$) => action$.pipe(
//   ofType(ActionTypes.acceptOffer),
//   withLatestFrom(state$),
//   flatMap(([ action, state ]) => {
//     return [
//       ApplicationRequestActions.acceptOfferRequest(state.leaseForm.selectedOffer)
//     ]
//   }),
// )

// const acceptOfferRequest = (action$, state$) => action$.pipe(
//   ofType(ActionTypes.acceptOfferRequest),
//   withLatestFrom(state$),
//   flatMap(([ action, state ]) => {
//     return from(ApplicationAPI.accept_the_offer(
//       state.widget.key,
//       action.payload.app_id,
//       { offer_id: action.payload.offer_id }
//     ))
//     .pipe(
//       flatMap(data => {
//         if (data.errors) {
//           return of(ApplicationRequestActions.acceptOfferFailed())
//         }

//         return of(ApplicationRequestActions.acceptOfferSuccess(data))
//       }),
//       catchError(err => {
//         return of(ApplicationRequestActions.acceptOfferFailed()
//       )}),
//     )
//   }),
// )


// Steps from which a Snap offer can be (re)selected: the offers list itself and
// the pending-verification screen that resumes the flow after Snap approves.
const SNAP_OFFER_STEPS = ['form3', 'snapPendingScreen'];

/////////////////////////////////////////////////////////////////
// set selected offer to reduser and call accept_the_offer for flexshopper
// should go to confirmationStep
const selectOffer = (action$, state$) => action$.pipe(
  ofType(ActionTypes.selectOffer),
  withLatestFrom(state$),
  flatMap(([action, state]) => {
    const selectedOffer = state.leaseForm.selectedOffer;
    const allSelectedOffers = state.leaseForm.allSelectedOffers;
    const requestBody = action.payload.body || action.payload.selectedOffer;

    // Show already opened contract
    if (allSelectedOffers.some(offer => offer.app_id === selectedOffer.app_id)) {
      return of(ApplicationRequestActions.selectOfferSuccess({selectedOffer: selectedOffer, isContractOpened: true}));
    }

    // Snap still needs identity / bank verification for this offer: ask Snap to
    // text the customer a link and park them on the pending screen. Once Snap
    // approves, SnapPendingScreen re-dispatches selectOffer with the fresh offer
    // and the regular snap flow below continues.
    if (
      selectedOffer.provider === 'snap' &&
      selectedOffer.pending_verification &&
      SNAP_OFFER_STEPS.includes(state.leaseForm.activeStep)
    ) {
      const pendingStepData = (extra) => ({
        ...selectedOffer,
        pendingSource: 'select',
        ...extra
      });
      return from(ProvidersApi.snapSendSmsLink(
        state.widget.key,
        { request_id: state.leaseForm.requestId, lease_app_id: selectedOffer.lease_app_id }
      )).pipe(
        flatMap(data => of(ApplicationRequestActions.nextStep({
          step: 'snapPendingScreen',
          data: pendingStepData({
            smsError: !data || !!data.errors,
            pending_message: data && data.pending_message
          })
        }))),
        catchError(() => of(ApplicationRequestActions.nextStep({
          step: 'snapPendingScreen',
          data: pendingStepData({ smsError: true })
        })))
      );
    }

    if (selectedOffer.provider === 'snap' && SNAP_OFFER_STEPS.includes(state.leaseForm.activeStep)) {
      return of(ApplicationRequestActions.nextStep({ step: 'snapLeasePayment', data: selectedOffer }));
    }
    if ((selectedOffer.provider === 'koalafi' && selectedOffer.type === 'Lease to Own') && state.leaseForm.activeStep === 'form3') {
      return of(ApplicationRequestActions.nextStep({ step: 'contractStep', data: selectedOffer }));
    }
    if (selectedOffer.bank_data_is_required) {
      const step = selectedOffer.provider === 'uown' ? 'uownBankDataForm' : 'bankDataForm';
      return of(ApplicationRequestActions.nextStep({ step, data: selectedOffer }));
    }
    if (selectedOffer.provider === 'progressive') {
      if (state.leaseForm.activeStep === 'progressivePrequalOffer' && selectedOffer.banking_data) {
        return of(ApplicationRequestActions.nextStep({ step: 'progressivePrequalEmployment', data: selectedOffer }));
      }
      if ((state.leaseForm.activeStep === 'form3' || state.leaseForm.activeStep === 'progressivePrequalEmployment') && selectedOffer.banking_data) {
        // Bank account already given on the application form: skip the bank
        // step. The card step and update_application still need it in the
        // offer data, where ProgressiveBankDataForm would have put it.
        const { additional_info_data } = state.leaseForm.applicationRequest;
        const bankAccount = additional_info_data && additional_info_data.bankAccount;
        if (bankAccount && bankAccount.routing && bankAccount.account) {
          return of(ApplicationRequestActions.nextStep({
            step: 'progressiveCardData',
            data: {
              ...selectedOffer,
              has_additional_info: true,
              bank_data_is_required: false,
              additional_info_data: { bankAccount }
            }
          }));
        }
        return of(ApplicationRequestActions.nextStep({ step: 'progressiveBankData', data: selectedOffer }));
      }
      if (state.leaseForm.activeStep === 'progressiveBankData' && selectedOffer.banking_data) {
        return of(ApplicationRequestActions.nextStep({ step: 'progressiveCardData', data: selectedOffer }));
      }
      if (!selectedOffer.banking_data && state.leaseForm.activeStep === 'progressivePrequalApproved' && selectedOffer.contractUrl) {
        return of(ApplicationRequestActions.selectOfferSuccess({
          selectedOffer: selectedOffer,
          isContractOpened: false
        }));
      }
      if (state.leaseForm.activeStep === 'progressiveCardData' || (!selectedOffer.banking_data && state.leaseForm.activeStep === 'form3')) {
        return of(ApplicationRequestActions.nextStep({ step: 'progressiveAgreement', data: selectedOffer }));
      }
    }

    if (selectedOffer.provider === 'kafene') {
      if (state.leaseForm.activeStep === 'form3') {
        return of(ApplicationRequestActions.nextStep({ step: 'kafeneAgreement', data: selectedOffer }));
      }
      if (state.leaseForm.activeStep === 'kafeneAgreement') {
        return of(ApplicationRequestActions.nextStep({ step: 'kafeneBankData', data: selectedOffer }));
      }
    }

    if (selectedOffer.provider === 'fypn') {
      if (state.leaseForm.activeStep === 'form3') {
        return of(ApplicationRequestActions.nextStep({ step: 'fypnAdditionalInfoForm', data: selectedOffer }));
      }
      if (state.leaseForm.activeStep === 'fypnAdditionalInfoForm') {
        return of(ApplicationRequestActions.nextStep({ step: 'fypnCardForm', data: selectedOffer }));
      }
      if (state.leaseForm.activeStep === 'fypnCardForm') {
        return of(ApplicationRequestActions.nextStep({ step: 'fypnBankDataForm', data: selectedOffer }));
      }
    }

    if (
      selectedOffer.provider === 'uown' && state.leaseForm.activeStep === 'uownBankDataForm'
    ) {
      return from(ApplicationAPI.accept_the_offer(
        state.widget.key,
        selectedOffer.app_id,
        requestBody || {}
      )).pipe(
        flatMap(data => {
          if (data.errors) {
            return of(ApplicationRequestActions.nextStep({step: 'uownUnavailableScreen'}));
          }
          const updatedOffer = {
            ...selectedOffer,
            ...data,
            contractUrl: data.options?.[0]?.contractUrl || selectedOffer.contractUrl,
            bank_data_is_required: false
          };
          return of(ApplicationRequestActions.selectOfferSuccess({selectedOffer: updatedOffer, isContractOpened: false}));
        }),
        catchError(err => {
          return of(ApplicationRequestActions.nextStep({step: 'uownUnavailableScreen'}));
        })
      );
    }

    if (
      selectedOffer.provider === 'creditkey' &&
      state.leaseForm.activeStep === 'bankDataForm'
    ) {
      return from(
        ProvidersApi.creditkeyValidatePayment(
          state.widget.key,
          action.payload.selectedOffer,
        )
      ).pipe(
        flatMap((data) => {
          if (data.errors) {
            return of(ApplicationRequestActions.paymentValidationFailed());
          }

          return from(
            ApplicationAPI.accept_the_offer(
              state.widget.key,
              selectedOffer.app_id,
              requestBody || {}
            )
          ).pipe(
            flatMap(data => {
              if (data.errors) {
                return of(ApplicationRequestActions.b2bOfferFailed());
              }
              return of(ApplicationRequestActions.selectOfferSuccess({selectedOffer: data, isContractOpened: false, token: data.token, body: requestBody}));
            }),
            catchError(err => {
              return of(ApplicationRequestActions.b2bOfferFailed());
            })
          )
        }),
        catchError((err) => {
          return of(ApplicationRequestActions.paymentValidationFailed());
        })
      );
    }

    return from(ApplicationAPI.accept_the_offer(
      state.widget.key,
      selectedOffer.app_id,
      requestBody || {}
    ))
      .pipe(
        flatMap(data => {
          if (data.errors) {
            return of(ApplicationRequestActions.selectOfferFailed());
          }
          return of(ApplicationRequestActions.selectOfferSuccess({selectedOffer: data, isContractOpened: false, token: data.token, body: requestBody}));
        }),
        catchError(err => {
          return of(ApplicationRequestActions.selectOfferFailed());
        })
      )
  }),
);

const selectOfferSuccess = (action$, state$) => action$.pipe(
  ofType(ActionTypes.selectOfferSuccess),
  withLatestFrom(state$),
  flatMap(([action, state]) => {
    const selectedOffer = state.leaseForm.selectedOffer;
    const isContractOpened = action.payload.isContractOpened || false;
    let nextStep = 'confirmationStep';

    // Snap pending verification (identity / bank step-up) after accepting the
    // offer: the customer has to follow the SMS link from Snap before the flow
    // can continue. SnapPendingScreen polls check_status and re-dispatches
    // selectOfferSuccess once Snap approves.
    if (
      selectedOffer.provider === 'snap' &&
      selectedOffer.data &&
      selectedOffer.data.pending === true
    ) {
      return of(ApplicationRequestActions.nextStep({
        step: 'snapPendingScreen',
        data: selectedOffer
      }));
    }

    // handle already opened contract
    if (isContractOpened) {
      nextStep = 'contractStep';
    } else {
      if (['aff', 'acima', 'koalafi', 'ownlease', 'progressive', 'zip', 'katapult2'].includes(selectedOffer.provider) || ['katapult', 'bread'].includes(selectedOffer.specific_provider_name)) {
        nextStep = 'contractStep';
      }
      if (selectedOffer.provider === 'uown') {
        nextStep = state.leaseForm.activeStep === 'uownBankDataForm' ? 'contractStep' : 'confirmationStep';
      }
      if (selectedOffer.provider === 'snap') {
        nextStep = 'snapConfirmation';
      }

      if (selectedOffer.provider === 'creditkey') {
        return from(ProvidersApi.creditkeyAgreement(
          state.widget.key,
          action.payload.body
        ))
          .pipe(
            flatMap(data => {
              if (data.errors) {
                return of(ApplicationRequestActions.b2bOfferFailed());
              }
              return of(ApplicationRequestActions.nextStep({ step: 'contractStep', data: selectedOffer, agreement: data }));
            }),
            catchError(err => {
              return of(ApplicationRequestActions.b2bOfferFailed());
            })
          )
      }

      if (selectedOffer.provider === 'flexshopper') {
        if (selectedOffer.bank_data === 'invalid') {
          return of(ApplicationRequestActions.selectOfferFailed({
            title: 'Thank You For Applying',
            message: 'You did not qualify for a spending limit. Thank you for your interest in FlexShopper. You will be sent an email with an explanation within 30 days.'
          }));
        }
        if (selectedOffer.bank_data_is_required) {
          nextStep = 'bankDataForm';
        } else if (selectedOffer.verification_is_required) {
          nextStep = 'verificationQuestionsStep';
        } else {
          nextStep = 'contractStep';
        }
      }

      if (selectedOffer.provider === 'fypn') {
        nextStep = selectedOffer.ibv_enabled ? 'fypnIbvFrame' : 'fypnContract';
      }
    }
    return of(ApplicationRequestActions.nextStep({ step: nextStep, data: selectedOffer }));
  })
);


// used on confirmation step
// does nothing for paytomorrow
// call accept_the_offer for flexshopper
// go to contract step for paytomorrow

const confirmOffer = (action$, state$) => action$.pipe(
  ofType(ActionTypes.confirmOffer),
  withLatestFrom(state$),
  flatMap(([ action, state ]) => {
    const selectedOffer = state.leaseForm.selectedOffer
    return from(ApplicationAPI.accept_the_offer(
      state.widget.key,
      selectedOffer.app_id,
      { offer_id: selectedOffer.offer_id }
    ))
      .pipe(
        flatMap(data => {
          if (data.errors) {
            return of(ApplicationRequestActions.confirmOfferFailed())
          }

          let activeStep = [
            "paytomorrow",
            "okinus",
            "uown",
            "ownlease",
            "kornerstone",
          ].includes(state.leaseForm.selectedOffer.provider)
            ? "contractStep"
            : "confirmationStep";

          return [
            ApplicationRequestActions.confirmOfferSuccess(data),
            ApplicationRequestActions.nextStep({ step: activeStep, data: data })
          ];

        }),
        catchError(err => {
          return of(ApplicationRequestActions.confirmOfferFailed()
          )}),
      )
  }),
);



///////////////////////////////////////

const cancelApplication = (action$, state$) => action$.pipe(
  ofType(ActionTypes.cancelApplication),
  withLatestFrom(state$),
  flatMap(([ action, state ]) => {
    return from(ApplicationAPI.cancel(
      state.widget.key,
      state.leaseForm.selectedOffer.app_id,
      {}
    ))
      .pipe(
        flatMap(data => {
          return [];
        }),
        catchError(err => {
          return [];
        })
      )
  })
)

const finishApplication = (action$, state$) => action$.pipe(
  ofType(ActionTypes.finishApplication),
  withLatestFrom(state$),
  flatMap(([ action, state ]) => {
    return from(ApplicationAPI.finish(
      state.widget.key,
      state.leaseForm.selectedOffer.app_id,
      action.payload && action.payload.status && {status: action.payload.status}
    ))
      .pipe(
        flatMap(data => {
          if (data.errors) {
            return of(ApplicationRequestActions.finishApplicationFailed())
          }

          let actions = !(action.payload && action.payload.status === 'exited') ? [
            ApplicationRequestActions.finishApplicationSuccess(),
            ApplicationRequestActions.nextStep({step: 'successStep'}),
            WidgetActions.finishFlow()
          ] : [];

          return actions.length > 0 ? from(actions) : EMPTY;
        }),
        catchError(err => {
          return of(ApplicationRequestActions.finishApplicationFailed())
        })
      )
  })
)

const processAuthUserResponse = (action$, state$) => action$.pipe(
  ofType(ActionTypes.processAuthUserResponse),
  withLatestFrom(state$),
  flatMap(([ action, state ]) => {
    // check how it will work on production. Maybe it will be not a string
    const prequalification = isPrequalification(state.leaseForm.applicationRequest.prequalification);
    const b2b = state.leaseForm.applicationRequest.b2b === true;
    const data = action.payload.response;
    const providerName = state.leaseForm.applicationRequest.provider_name;

    if ((!state.leaseForm.applicationRequest || !state.leaseForm.applicationRequest.personalInfo ||
      !state.leaseForm.applicationRequest.personalInfo.email) && !prequalification && !b2b) {
      return of(ApplicationRequestActions.selectOfferFailed({ error_form: 'wrong_data' }));
    }

    if (!data.request) {
      if (b2b && !prequalification) return EMPTY;

      if (providerName === 'progressive' && !prequalification && !b2b) {
        return of(ApplicationRequestActions.nextStep({ step: 'progressiveCustomerLookup' }));
      }

      const step = prequalification ? 'prequalification' : 'form2';
      return of(ApplicationRequestActions.nextStep({ step }));
    }

    if (data.can_be_updated) {
      return of(ApplicationRequestActions.updateApplication({ request_id: data.request.id }));
    }

    if (data.already_completed) {
      return of(
        data.already_completed.provider === 'paytomorrow'
          ? ApplicationRequestActions.nextStep({ step: 'alreadyCompletedStep', data: { app: data.already_completed } })
          : ApplicationRequestActions.updateCompletedApplication({ request_id: data.request.id })
      );
    }

    if (data.all_declined) {
      return of(ApplicationRequestActions.nextStep({ step: 'nobodyApprovedStep' }));
    }

    if (b2b && !prequalification) {
      return EMPTY;
    }

    if (providerName === 'progressive' && !prequalification && !b2b) {
      return of(ApplicationRequestActions.nextStep({ step: 'progressiveCustomerLookup' }));
    }

    const step = prequalification ? 'prequalification' : 'form2';
    return of(ApplicationRequestActions.nextStep({ step }));
  })
);

const updateApplication = (action$, state$) => action$.pipe(
  ofType(ActionTypes.updateApplication),
  withLatestFrom(state$),
  flatMap(([ action, state ]) => {
    return from(ApplicationRequestAPI.update(
      state.widget.key,
      action.payload.request_id,
      {application: state.leaseForm.applicationRequest}
    ))
      .pipe(
        flatMap(data => {

          const discountOffers = checkDiscount(data, state);
          if (discountOffers.offers.length > 0 && !state.leaseForm.applicationRequest.promoApplied) {
            return [
              ApplicationRequestActions.applyDiscount({data, discountOffers})
            ]
          }
          return [
            ApplicationRequestActions.initApplicationSuccess({data}),
            ApplicationRequestActions.processApplicationResponse(data)
          ]
        }),
        catchError(err => {
          return of(ApplicationRequestActions.initApplicationFailed()
          )}),
      )
  }),
);

const updateCompletedApplication = (action$, state$) => action$.pipe(
  ofType(ActionTypes.updateCompletedApplication),
  withLatestFrom(state$),
  flatMap(([ action, state ]) => {
    return from(ApplicationRequestAPI.updateCompletedApplication(
      state.widget.key,
      action.payload.request_id,
      {application: state.leaseForm.applicationRequest}
    ))
      .pipe(
        flatMap(data => {
          if (data.errors) {
            return of(ApplicationRequestActions.initApplicationFailed())
          }
          return [
            ApplicationRequestActions.initApplicationSuccess({data}),
            ApplicationRequestActions.processApplicationResponse(data)
          ]
        }),
        catchError(err => {
          return of(ApplicationRequestActions.initApplicationFailed()
          )}),
      )
  }),
);


// const processUpdateCompletedApplicationResponse = (action$, state$) => action$.pipe(
//   ofType(ActionTypes.processUpdateCompletedApplicationResponse),
//   withLatestFrom(state$),
//   flatMap(([ action, state ]) => {
//     const data = action.payload

//     if (data.offers.length === 0) {
//       return of(ApplicationRequestActions.nextStep({step: 'nobodyApprovedStep'}))
//     }

//     // const approved_not_finished = data.offers.filter(o => o.approve_status === 'approved' && o.is_finished === false);
//     // const need_additionals = data.offers.filter(o => o.approve_status === 'need_additionals');
//     const finished = data.offers.find(o => o.is_finished === true);

//     // if (finished) {
//     //   return of(ApplicationRequestActions.nextStep({
//     //     step: 'alreadyCompletedStep',
//     //     data: { app: finished }
//     //   }))
//     // }

//     // if (approved_not_finished.length > 0) {
//     //   return of(ApplicationRequestActions.nextStep({step: 'form4'}))
//     // }

//     // if (need_additionals.length > 0) {
//     //   return of(ApplicationRequestActions.nextStep({step:'additionalInfoStep'}))
//     // }

//     return of(ApplicationRequestActions.nextStep({step:'nobodyApprovedStep'}))
//   })
// );


const checkPrequalification = (action$, state$) => action$.pipe(
  ofType(ActionTypes.processAuthUserResponse),
  withLatestFrom(state$),
  flatMap(([action, state]) => {
    const b2b = state.leaseForm.applicationRequest.b2b === true;
    const prequalification = isPrequalification(state.leaseForm.applicationRequest.prequalification);
    const phone = state.leaseForm.applicationRequest.personalInfo.phone;

    if (b2b && !prequalification && phone) {
      return from(UsersAPI.checkPrequalification(state.widget.key, phone))
        .pipe(
          flatMap(data => {
            if (data.prequalification_found) {
              const prequalSuccess = ApplicationRequestActions.checkPrequalificationSuccess({
                prequalification_found: true,
                company_id: data.company_id,
                app_id: data.app_id
              });

              let applicationRequest = {
                provider_name: 'creditkey',
                b2b: true,
                personalInfo: {
                  phone: phone
                }
              };

              if (data.company_id) {
                applicationRequest.companyId = data.company_id;
                const nextStepAction = ApplicationRequestActions.nextStep({ step: 'form3' });
                const initAction = ApplicationRequestActions.initApplication({ values: applicationRequest });

                return [
                  prequalSuccess,
                  initAction
                ];
              }

              return of(ApplicationRequestActions.checkPrequalificationFailed());
            } else {
              return of(
                ApplicationRequestActions.checkPrequalificationFailed(),
                ApplicationRequestActions.nextStep({ step: 'form2' })
              );
            }
          }),
          catchError(err => {
            return of(
              ApplicationRequestActions.checkPrequalificationFailed(),
              ApplicationRequestActions.nextStep({ step: 'form2' })
            );
          })
        );
    }

    return EMPTY;
  })
);

export default combineEpics(
  applyDiscount,
  init,
  initApplication,
  selectOffer,
  selectOfferSuccess,
  confirmOffer,
  cancelApplication,
  finishApplication,
  continueInitApplication,
  updateApplication,
  processApplicationResponse,
  processAuthUserResponse,
  updateCompletedApplication,
  updateWithDiscount,
  getRecommendationsForItem,
  editCart,
  prequalEditCart,
  updateWithRecommendation,
  checkPrequalification
)
